import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { authService } from '../services/authService';
import { userService } from '../services/database/userService';
import { AuthUser, CompanyData } from '../types/auth';
import { isBlockedCompanyStatus } from './authGating';
import { useTheme } from './ThemeContext';
import { resolvePersistedTheme } from './themePersistence';

interface AuthContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (email: string, companyData: CompanyData, language: string) => Promise<{ id: string }>;
  logout: () => Promise<void>;
  checkEmailVerification: () => Promise<boolean>;
  refreshEmailVerification: () => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [companyDataLoading, setCompanyDataLoading] = useState(false);
  const { setDarkMode } = useTheme();

  // Seed the theme from the signed-in user's persisted preference so it follows
  // them onto a fresh device. The local (localStorage) choice is authoritative
  // and is NEVER overridden on refresh — see resolvePersistedTheme — so the
  // theme the user last set stays fixed until they toggle it themselves.
  // Failures are silently ignored — the local/default theme remains in effect.
  // [P2 #11]
  const applyPersistedTheme = useCallback(async (uid: string) => {
    // Fast path: a local choice already wins, so skip the Firestore read.
    if (localStorage.getItem('theme') !== null) return;
    try {
      const userDoc = await userService.getUser(uid);
      const toApply = resolvePersistedTheme(
        localStorage.getItem('theme'),
        userDoc?.preferences?.darkMode,
      );
      if (toApply !== null) {
        setDarkMode(toApply);
      }
    } catch {
      /* ignore — keep current theme */
    }
  }, [setDarkMode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        try {
          // Najprv nastavíme základné údaje používateľa
          const authUser = {
            uid: user.uid,
            email: user.email || '',
            emailVerified: user.emailVerified,
            companyData: undefined
          };
          setCurrentUser(authUser);

          // Potom načítame údaje firmy
          setCompanyDataLoading(true);
          const companyData = await authService.getCompanyData(user.uid);

          // Kontrola statusu firmy — blocked (rejected/inactive/suspended) → odhlásiť.
          // pending_verification ostáva prihlásiteľný (čaká na schválenie adminom).
          if (companyData && isBlockedCompanyStatus(companyData.status)) {
            await authService.logout();
            setCurrentUser(null);
            setCompanyDataLoading(false);
            return;
          }

          // Aktualizujeme používateľa s údajmi firmy
          setCurrentUser({
            ...authUser,
            companyData
          });
          setCompanyDataLoading(false);

          // Apply persisted per-user theme preference (cross-device).
          applyPersistedTheme(user.uid);
        } catch (error) {
          console.error('Chyba pri načítaní údajov používateľa:', error);
          // Aj keď sa nepodarí načítať údaje firmy, nastavíme základné údaje používateľa
          setCurrentUser({
            uid: user.uid,
            email: user.email || '',
            emailVerified: user.emailVerified,
            companyData: undefined
          });
          setCompanyDataLoading(false);
        }
      } else {
        setCurrentUser(null);
        setCompanyDataLoading(false);
      }

      setLoading(false);
      setInitialized(true);
    });

    return unsubscribe;
  }, [applyPersistedTheme]);

  // Real-time company-status gate: if the signed-in user's company becomes
  // blocked (rejected/inactive/suspended) mid-session, sign them out immediately
  // instead of waiting for the next reload. Listener errors are ignored so a
  // transient/permission hiccup never logs out a legitimate user.
  useEffect(() => {
    const companyId = currentUser?.companyData?.id;
    if (!companyId) return;
    const unsub = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        if (isBlockedCompanyStatus(snap.data()?.status as string | undefined)) {
          authService.logout().finally(() => setCurrentUser(null));
        }
      },
      () => { /* ignore listener errors — do not sign out on transient failures */ }
    );
    return unsub;
  }, [currentUser?.companyData?.id]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const user = await authService.login(email, password);
      setCurrentUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  }, []);

  // Verify-before-create: iba uloží pending registráciu + pošle kód e-mailom.
  // Žiadne prihlásenie tu nenastáva — účet vznikne až na /verify-email/:id.
  const register = useCallback(async (email: string, companyData: CompanyData, language: string) => {
    return authService.registerCompany(email, companyData, language);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setCurrentUser(null);
    } catch (error) {
      throw error;
    }
  }, []);

  const checkEmailVerification = useCallback(async (): Promise<boolean> => {
    return await authService.checkEmailVerification();
  }, []);

  // Re-check Firebase email verification (reloads the user) and, when it has
  // become verified, update the in-memory session so the central app-shell gate
  // re-evaluates and lets the user in without a full page reload. [auth gating]
  const refreshEmailVerification = useCallback(async (): Promise<boolean> => {
    const verified = await authService.checkEmailVerification();
    if (verified) {
      setCurrentUser(prev => (prev ? { ...prev, emailVerified: true } : prev));
    }
    return verified;
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    try {
      await authService.sendPasswordReset(email);
    } catch (error) {
      throw error;
    }
  }, []);

  const refreshUserData = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      setCompanyDataLoading(true);
      const companyData = await authService.getCompanyData(auth.currentUser.uid);
      setCurrentUser(prev => prev ? { ...prev, companyData } : null);
    } catch (error) {
      console.error('Chyba pri obnovovaní údajov:', error);
    } finally {
      setCompanyDataLoading(false);
    }
  }, []);

  const value: AuthContextType = useMemo(() => ({
    currentUser,
    loading: loading || !initialized || companyDataLoading,
    login,
    register,
    logout,
    checkEmailVerification,
    refreshEmailVerification,
    sendPasswordReset,
    refreshUserData
  }), [currentUser, loading, initialized, companyDataLoading, login, register, logout, checkEmailVerification, refreshEmailVerification, sendPasswordReset, refreshUserData]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 